import type { HqEmphasisActivity, HqEmphasisDraft, HqHistoricalSummary } from '@kop/contracts';

export const activityTypes = [
  ['inspection', '본사 지도점검'],
  ['drill', '재해대응훈련'],
  ['campaign', '안전캠페인'],
  ['contest', '안전공모전'],
] as const;
export const activityLabel = (value: HqEmphasisActivity['type']) =>
  activityTypes.find(([id]) => id === value)![1];
export const divisionLabel = (value: HqEmphasisActivity['division']) =>
  value ? { production: '생산부문', construction: '시공부문', marine: '해양부문' }[value] : '공통';
export const activityScopeKey = (row: HqEmphasisActivity) =>
  row.siteId ?? (row.division ? `division:${row.division}` : 'common');
export const activityCover = (row: HqEmphasisActivity) =>
  row.attachments.find(
    (file) => file.kind === 'photo' && file.manifestId === row.coverManifestId,
  ) ?? row.attachments.find((file) => file.kind === 'photo');
export function emphasisCounts(activities: readonly HqEmphasisActivity[]) {
  const inspections = activities.filter((row) => row.type === 'inspection');
  const sum = (key: 'findings' | 'actions') =>
    inspections.length && inspections.every((row) => row[key] !== null)
      ? inspections.reduce((total, row) => total + row[key]!, 0)
      : null;
  const findings = sum('findings'),
    actions = sum('actions');
  return {
    findings,
    actions,
    outstanding: findings !== null && actions !== null ? findings - actions : null,
    rate: findings && actions !== null ? Math.round((actions / findings) * 100) : null,
    drill: activities.filter((row) => row.type === 'drill').length,
    campaign: activities.filter((row) => row.type === 'campaign').length,
    contest: activities.filter((row) => row.type === 'contest').length,
  };
}
export function newEmphasisDraft(year: number, half: 1 | 2): HqEmphasisDraft {
  return {
    year,
    half,
    title: `${year}년 ${half === 1 ? '상반기' : '하반기'} 안전강조주간`,
    eventFrom: null,
    eventTo: null,
    owner: '',
    objective: '',
    siteIds: [],
    activities: [],
    historicalReferenceId: null,
  };
}
/** Original reports remain immutable; unknown dates, site mappings and action counts stay unknown. */
export function historicalEmphasisDraft(
  historical: HqHistoricalSummary | null,
  year: number,
  half: 1 | 2,
  sites: readonly { id: string; name: string }[],
): HqEmphasisDraft {
  const draft = newEmphasisDraft(year, half);
  const report =
    historical?.year === year ? historical.emphasis.find((row) => row.half === half) : null;
  if (!report) return draft;
  draft.title = `${year}년 ${half === 1 ? '상반기' : '하반기'} 제${report.round}회 안전강조주간`;
  draft.eventFrom = report.eventFrom;
  draft.eventTo = report.eventTo;
  draft.historicalReferenceId = report.id;
  draft.activities = [
    ...report.siteInspections.map((row, i): HqEmphasisActivity => ({
      id: `source-inspection-${i}`,
      type: 'inspection',
      siteId: sites.some((site) => site.id === row.siteId) ? row.siteId : null,
      title: '사업장 안전점검',
      date: row.findingDate,
      owner: '',
      note: '',
      findings: row.findings,
      actions: row.actions,
      attachments: [],
      coverManifestId: null,
    })),
    ...(['trainingActivities', 'campaignActivities'] as const).flatMap((key) =>
      report[key].map((row, i): HqEmphasisActivity => ({
        id: `source-${key}-${i}`,
        type: key === 'trainingActivities' ? 'drill' : 'campaign',
        siteId: sites.some((site) => site.id === row.siteId) ? row.siteId : null,
        division:
          row.division === 'production' ||
          row.division === 'construction' ||
          row.division === 'marine'
            ? row.division
            : null,
        title: row.title,
        date: row.date,
        owner: '',
        note: row.reportedState ?? '',
        findings: null,
        actions: null,
        attachments: [],
        coverManifestId: null,
      })),
    ),
    ...report.contests.map((row, i): HqEmphasisActivity => ({
      id: `source-contest-${i}`,
      type: 'contest',
      siteId: null,
      title: row.title,
      date: null,
      owner: '',
      note: `접수 ${row.submissions}건`,
      findings: null,
      actions: null,
      attachments: [],
      coverManifestId: null,
    })),
  ];
  draft.siteIds = [
    ...new Set(draft.activities.map((row) => row.siteId).filter((id): id is string => !!id)),
  ];
  return draft;
}
